/**
 * Filter Comparison Utilities
 * 
 * Provides consistent filter comparison logic across all components.
 * Ensures filters are compared by both name and selected values, not just name.
 */

/**
 * Normalizes filter selected values for consistent comparison
 * Handles ObjectIds, arrays, and various data types
 */
export const normalizeFilterValue = (value: any): string => {
  if (Array.isArray(value)) {
    return value
      .map((v: any) => String(v?._id || v || ''))
      .sort()
      .join(',');
  }
  return String(value?._id || value || '');
};

/**
 * Creates a unique key for a filter based on name and selected values
 * Format: "filterName::normalizedValues"
 */
export const getFilterKey = (filter: { name: string; selected?: any }): string => {
  const selectedKey = normalizeFilterValue(filter.selected);
  return `${filter.name}::${selectedKey}`;
};

/**
 * Checks if two filters are equivalent (same name and selected values)
 */
export const areFiltersEquivalent = (
  filter1: { name: string; selected?: any },
  filter2: { name: string; selected?: any }
): boolean => {
  return getFilterKey(filter1) === getFilterKey(filter2);
};

/**
 * Filters out view default filters from activeFilters
 * Returns only user-added filters (filters not in view's filters)
 */
export const getUserAddedFilters = (
  activeFilters: Array<{ name: string; selected?: any }>,
  viewFilters?: Array<{ name: string; selected?: any }>
): Array<{ name: string; selected?: any }> => {
  if (!viewFilters || !Array.isArray(viewFilters) || viewFilters.length === 0) {
    return activeFilters; // All filters are user-added if no view
  }

  // Create set of view filter keys
  const viewFilterKeys = new Set<string>();
  viewFilters.forEach((viewFilter) => {
    viewFilterKeys.add(getFilterKey(viewFilter));
  });

  // Return only filters that don't match view filters exactly
  return activeFilters.filter((activeFilter) => {
    const activeFilterKey = getFilterKey(activeFilter);
    return !viewFilterKeys.has(activeFilterKey);
  });
};

/**
 * Gets filters that match view default filters exactly (by name + values)
 * Returns filters to keep when clearing user-added filters
 * Preserves all filter properties from the original activeFilters
 */
export const getViewDefaultFilters = <T extends { name: string; selected?: any }>(
  activeFilters: T[],
  viewFilters?: Array<{ name: string; selected?: any }>
): T[] => {
  if (!viewFilters || !Array.isArray(viewFilters) || viewFilters.length === 0) {
    return []; // No view filters to keep
  }

  // Create set of view filter keys
  const viewFilterKeys = new Set<string>();
  viewFilters.forEach((viewFilter) => {
    viewFilterKeys.add(getFilterKey(viewFilter));
  });

  // Return only filters that match view filters exactly, preserving all properties
  return activeFilters.filter((activeFilter) => {
    const activeFilterKey = getFilterKey(activeFilter);
    return viewFilterKeys.has(activeFilterKey);
  });
};

/**
 * Checks if all active filters match view filters exactly
 */
export const areAllFiltersFromView = (
  activeFilters: Array<{ name: string; selected?: any }>,
  viewFilters?: Array<{ name: string; selected?: any }>
): boolean => {
  if (!viewFilters || !Array.isArray(viewFilters) || viewFilters.length === 0) {
    return activeFilters.length === 0;
  }

  if (activeFilters.length !== viewFilters.length) {
    return false;
  }

  const viewFilterKeys = new Set<string>();
  viewFilters.forEach((viewFilter) => {
    viewFilterKeys.add(getFilterKey(viewFilter));
  });

  // All active filters must match view filters
  return activeFilters.every((activeFilter) => {
    const activeFilterKey = getFilterKey(activeFilter);
    return viewFilterKeys.has(activeFilterKey);
  });
};

/**
 * Checks if there are any user-added filters (filters not in view)
 */
export const hasUserAddedFilters = (
  activeFilters: Array<{ name: string; selected?: any }>,
  viewFilters?: Array<{ name: string; selected?: any }>
): boolean => {
  const userAddedFilters = getUserAddedFilters(activeFilters, viewFilters);
  return userAddedFilters.length > 0;
};
